import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { formatDuration } from '../playlists/Playlists.types';
import { onDemandApi } from './OnDemand.api';
import { ON_DEMAND_KIND_LABEL, OnDemandPlaylist } from './OnDemand.types';

const REFRESH_MS = 10_000;

interface OnDemandQueueProps {
    screenId: string;
    /** Bumped by the wizard after a successful schedule so the queue updates immediately. */
    refreshToken: number;
    onCount?: (count: number) => void;
}

const playOrder = (rows: OnDemandPlaylist[]) =>
    [...rows].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

/** Panel: every queued playlist on this screen, oldest first, refreshed every 10 seconds. */
export const OnDemandQueue: React.FC<OnDemandQueueProps> = ({ screenId, refreshToken, onCount }) => {
    const [playlists, setPlaylists] = useState<OnDemandPlaylist[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [now, setNow] = useState(() => Date.now());

    const load = useCallback(async () => {
        try {
            const rows = playOrder(await onDemandApi.list(screenId, 'queued'));
            setPlaylists(rows);
            onCount?.(rows.length);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load the queue');
        } finally {
            setLoading(false);
        }
    }, [screenId, onCount]);

    useEffect(() => {
        void load();
        const timer = window.setInterval(() => {
            setNow(Date.now());
            void load();
        }, REFRESH_MS);
        return () => window.clearInterval(timer);
    }, [load, refreshToken]);

    const cards = useMemo(() => {
        let cursor = now;
        return playlists.map((playlist, index) => {
            const totalMs = Math.max(0, playlist.totalDurationMs) * Math.max(1, playlist.maxPlays || 1);
            const remainingMs = Math.max(0, playlist.totalDurationMs) * Math.max(0, (playlist.maxPlays || 1) - (playlist.playCount || 0));
            const start = new Date(cursor);
            cursor += remainingMs || totalMs;
            const kind = ON_DEMAND_KIND_LABEL[playlist.kind] || playlist.kind;
            return {
                id: playlist._id,
                position: index + 1,
                name: playlist.name,
                item: `${playlist.items.length} ${kind.toLowerCase()}`,
                total: formatDuration(totalMs),
                start: start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            };
        });
    }, [playlists, now]);

    return (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2, height: '100%' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Queued on this screen</Typography>
                <Tooltip title="Refresh">
                    <Button variant="contained" color="secondary" size="small" onClick={() => void load()} aria-label="Refresh queue" sx={{ minWidth: 36, px: 1 }}>
                        <Refresh fontSize="small" />
                    </Button>
                </Tooltip>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && playlists.length === 0 ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress color="secondary" /></Box>
            ) : cards.length === 0 ? (
                <Alert severity="info">Nothing is queued for this screen.</Alert>
            ) : (
                <Box display="flex" flexDirection="column" gap={1}>
                    {cards.map((card) => (
                        <Paper key={card.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                            <Typography variant="overline" color="text.secondary">Queue {card.position}</Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{card.name}</Typography>
                            <Typography variant="body2">Item: {card.item}</Typography>
                            <Typography variant="body2">Total time: {card.total}</Typography>
                            <Typography variant="body2">Estimated start: {card.start}</Typography>
                        </Paper>
                    ))}
                </Box>
            )}
        </Paper>
    );
};
